import React, { useEffect, useRef } from 'react';
import { alignMap } from '../../utils/leafletUtil';

/* Leaflet */
import { LatLngLiteral, Map, TileLayer as TileLayerType } from 'leaflet';

import {
  MapContainer,
  Polygon,
  TileLayer,
  MapContainerProps,
} from 'react-leaflet';

import { useTheme } from '@wegroup/design-system';

/**
 * @description
 * Note the difference in the "lyrs" parameter in the URL:
 * Hybrid: s,h;
 * Satellite: s;
 * Streets: m;
 * Terrain: p;
 */

const mapTypes = {
  STREETS: 'http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
  HYBRID: 'http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
  SATELLITE: 'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
  TERRAIN: 'http://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
};

enum MapType {
  STREETS = 'STREETS',
  HYBRID = 'HYBRID',
  SATELLITE = 'SATELLITE',
  TERRAIN = 'TERRAIN',
}

export interface LeafletGoogleMapProps {
  /* positioning */
  centerCoords: LatLngLiteral;
  centerAlignment?: 'left' | 'right' | 'center';
  markerCoords?: LatLngLiteral[][];

  /* booleans */
  isEnabledNativeZoom?: boolean;
  isEnabledNativeGrab?: boolean;

  /* zoom */
  initialZoom?: number;
  maxZoom?: number;
  minZoom?: number;
}

interface Props {
  mapType: keyof typeof MapType;
}

const LeafletGoogleMap: React.FC<
  React.PropsWithChildren<Props & LeafletGoogleMapProps>
> = ({
  children,
  mapType,

  /* positioning */
  centerCoords,
  markerCoords,
  centerAlignment = 'center',

  /* booleans */
  isEnabledNativeZoom,
  isEnabledNativeGrab,

  /* zoom */
  initialZoom = 19,
  maxZoom = 21,
  minZoom = 17,
}) => {
  const { colors } = useTheme();

  /* 
  Usage of useEffect and ref is mandatory,
  as the "url" is immutable in the TileLayer component itself
  */

  const tileRef = useRef<TileLayerType>(null);
  useEffect(() => {
    tileRef.current && tileRef.current.setUrl(mapTypes[mapType]);
  }, [mapType]);

  /* Feature props */
  const disableGrabProps: MapContainerProps = {
    tap: false,
    dragging: false,
  };

  const disableZoomProps: MapContainerProps = {
    scrollWheelZoom: false,
    doubleClickZoom: false,
    touchZoom: false,
  };

  const handleMapCreated = (map: Map | null) => {
    if (map) {
      alignMap(map, centerAlignment, centerCoords);
      map.addEventListener('resize', () =>
        alignMap(map, centerAlignment, centerCoords),
      );
    }
  };

  return (
    <MapContainer
      zoom={initialZoom}
      zoomControl={false} // Disable native zoom buttons, they don't fit our design
      center={centerCoords}
      ref={handleMapCreated}
      /* Map always needs a height and width, otherwise it won't render */
      style={{
        height: '100%',
        width: '100%',
        zIndex: 0,
      }}
      attributionControl={false}
      {...(!isEnabledNativeZoom && disableGrabProps)} // We use our own zoom buttons, not enabled by default
      {...(!isEnabledNativeGrab && disableZoomProps)} // Grab usually not necessary, not enabled by default
    >
      {children}
      <TileLayer
        maxZoom={maxZoom}
        minZoom={minZoom}
        url={mapTypes[mapType]}
        subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
        ref={tileRef}
      />
      {markerCoords && (
        <Polygon positions={markerCoords} color={colors.primary[500]} />
      )}
    </MapContainer>
  );
};

export default LeafletGoogleMap;
